import usePromise from "react-use-promise"

import { useAuth } from "../common/AuthProvider"
import { useEsriModules } from "../common/useEsriModules"
import { usePortal } from "../common/usePortal"

export type GroupDataShape = {
  group: __esri.PortalGroup
  featureLayers: __esri.FeatureLayer[]
}

export const usePortalGroups = () => {
  /** Get portal and username so we can grab the right content */
  const portal = usePortal()
  const { username } = useAuth()

  /** Get esri modules */
  const [esriRequest, PortalQueryParams] = useEsriModules<
    [typeof __esri.request, typeof __esri.PortalQueryParams]
  >(["esri/request", "esri/portal/PortalQueryParams"])

  /** Query the groups and their feature layers */
  return usePromise<GroupDataShape[]>(() => {
    /** Return early if portal is undefined. It usually means we're not logged in yet. */
    if (!portal) return Promise.reject("No portal available")

    /** Return early if esri modules haven't loaded */
    if (!esriRequest || !PortalQueryParams)
      return Promise.reject("Esri modules not loaded yet.")

    /** Groups aggregator (so the output is the right shape) */
    const groups: GroupDataShape[] = []

    /** First request - get the groups the user has access to */
    return (
      esriRequest(portal.url + "/sharing/rest/community/groups", {
        authMode: "immediate",
        query: {
          f: "json",
          q: "",
          searchUserAccess: "groupMember",
          searchUserName: username,
          num: 100,
        },
      })
        .then(({ data }) =>
          Promise.all(
            /** For each group, grab the related feature layers. */
            data.results.map((group: __esri.PortalGroup) =>
              portal
                .queryItems(
                  new PortalQueryParams({
                    query: `group:${group.id} type:("Feature Collection" OR "Feature Service")`,
                    sortField: "modified",
                    sortOrder: "desc",
                    num: 20,
                  })
                )
                /** Push the group and features into the aggregator */
                .then(({ results }) =>
                  groups.push({
                    group,
                    featureLayers: results as __esri.FeatureLayer[],
                  })
                )
            )
          )
        )
        /** Finally, when all promises have resolved, return the aggregator. */
        .then(() => groups)
    )
  }, [portal, username, esriRequest, PortalQueryParams])
}
